// @ts-nocheck

import { isBetEligible } from '@winamax/missions-lib';
import { createSelector } from 'reselect';

import {
  selectBasketIsCashout,
  selectBasketItems,
  selectBasketItemsEntity,
  selectBasketMode,
  selectBasketSelectedFreebetId,
  selectBasketState,
  selectBasketStatus,
} from '../../../../selectors/basket/basketPrimary.selector';
import { selectBasketShowResume } from '../../../../selectors/basketCommon.selector';
import { selectBasketEnhancedItems } from '../../../../selectors/basketItems.selector';
import { selectEntitiesBets } from '../../../../selectors/entities/bets/entitiesBetsPrimary.selector';
import { selectEntitiesState } from '../../../../selectors/entities/entitiesPrimary.selector';
import { selectEntitiesMatches } from '../../../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import { selectUserConnected } from '../../../../selectors/user/userPrimary.selector';
import { type BasketItems } from '../../../../states/basket';
import { type BetState } from '../../../../states/entities/bets';
import { type MatchState } from '../../../../states/entities/matches';
import { type BasketMode, BasketModes, BasketStatuses } from '../../../../types/basket';
import { BetTypes } from '../../../../types/bet';
import { BetslipStatuses } from '../../../../types/betslip';
import { basketItemSetKLEligibleCollectible, basketSetKLEligibleCollectible } from '../../actions/basket';
import { KLErrorLabelsByErrorCode } from '../../constants/labels';
import {
  type KLUserBetslipState,
  type KLUserBadgesState,
  type KLUserOrientationCollectiblesState,
  type KLUserOrientationCollectibleState,
} from '../../reducers/kl';
import { type KLUserBadgeState } from '../../reducers/kl';
import { type KLUserPoleState } from '../../reducers/kl';
import {
  KLCollectibleStatuses,
  KLRound1CollectibleIds,
  KLRound2CollectibleIds,
  KLRound3CollectibleIds,
} from '../../types/collectible';
import { type KLCollectibleId } from '../../types/collectible';
import { KLBasketErrorCodes } from '../../types/errorCode';
import { KLGlobalStatuses } from '../../types/global';
import { KLRounds } from '../../types/round';
import { type KLRound } from '../../types/round';
import { KLUserStatuses } from '../../types/user';
import { selectKLGlobalCriteria, selectKLGlobalStatus, selectKLOperationEndDate } from '../global';
import { selectIsKLEnabled } from '../runtime';
import {
  selectKLUserBadges,
  selectKLUserBetslips,
  selectKLUserCollars,
  selectKLUserOrientation,
  selectKLUserPole,
  selectKLUserRound,
  selectKLUserStatus,
  selectKLUserVotes,
} from '../user';

const getOddsIdFromProps = (_, { oddsId }) => oddsId ?? null;
const getDispatchFromProps = (_, { dispatch }) => dispatch;

const selectUserEligibilities = createSelector(
  [selectKLGlobalCriteria, selectKLUserRound, selectKLUserBadges],
  (criteria, userRound): Array<unknown> => {
    if (userRound === KLRounds.T_RED) {
      return KLRound1CollectibleIds.reduce(
        (acc, collectibleId) =>
          criteria[collectibleId]
            ? [...acc, { id: collectibleId, eligibility: criteria[collectibleId].eligibility }]
            : acc,
        [],
      );
    }

    if (userRound === KLRounds.T_WHITE) {
      return KLRound2CollectibleIds.reduce(
        (acc, collectibleId) =>
          criteria[collectibleId]
            ? [...acc, { id: collectibleId, eligibility: criteria[collectibleId].eligibility }]
            : acc,
        [],
      );
    }

    if (criteria[userRound]?.eligibility) {
      return [{ id: userRound, eligibility: criteria[userRound].eligibility }];
    }

    return [];
  },
);

const getCollectibleIdsFromCriteria = (eligibilities, basket, item, entities) =>
  eligibilities.reduce(
    (acc, { id, eligibility }) =>
      isBetEligible({
        eligibility,
        basket,
        item,
        entities,
      })
        ? [...acc, id]
        : acc,
    [],
  );

const selectEligibleCollectibleIdsFromCriteria = createSelector(
  [
    selectBasketMode,
    selectBasketItems,
    getOddsIdFromProps,
    selectUserEligibilities,
    selectBasketState,
    selectEntitiesState,
  ],
  (basketMode, basketItems, oddsId, eligibilities, basket, entities): Array<KLCollectibleId | KLRound> => {
    const isModeSimple = basketMode === BasketModes.SIMPLE;
    const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;
    const item = basketItems[oddsId];

    if (item) {
      return getCollectibleIdsFromCriteria(eligibilities, basket, item, entities);
    } else {
      if (isOneSimpleBet) {
        const foundOddsId = Object.keys(basketItems)[0];

        if (foundOddsId) {
          const foundItem = basketItems[foundOddsId];

          if (foundItem) {
            return getCollectibleIdsFromCriteria(eligibilities, basket, foundItem, entities);
          }
        }
      } else {
        return getCollectibleIdsFromCriteria(eligibilities, basket, undefined, entities);
      }

      return [];
    }
  },
);

const selectBasketItemsMatches = createSelector(
  [selectEntitiesMatches, selectBasketItemsEntity, selectBasketItems],
  (matchesState, basketItem, basketItems) => {
    if (basketItem) {
      return [matchesState[basketItem.matchId]].filter((k) => k);
    }

    return Object.keys(basketItems)
      .map((key) => matchesState[basketItems[key]?.matchId])
      .filter((k) => k);
  },
);

const hasUserKLBetslipMatchingPendingBetslip = (
  userKLBetslipsAsArray: Array<KLUserBetslipState>,
  userKLRound: KLRound,
  userKLBadges: KLUserBadgesState,
  userKLOrientation: Array<KLUserOrientationCollectiblesState>,
  userKLPole: KLUserPoleState,
  basketItemMatchId: number,
) =>
  !!userKLBetslipsAsArray.find((userKLBetslip) => {
    if (userKLRound === KLRounds.T_RED) {
      const badges: Array<KLUserBadgeState> = Object.keys(userKLBadges)
        .filter((badgeId) => !!~KLRound1CollectibleIds.indexOf(badgeId))
        .map((badgeId) => userKLBadges[badgeId]);

      return badges.find(
        (badge) =>
          badge.betslipReference === userKLBetslip.betslipReference &&
          userKLBetslip.betslipStatus === BetslipStatuses.RUNNING &&
          !!~userKLBetslip.matchIds.indexOf(basketItemMatchId),
      );
    }

    if (userKLRound === KLRounds.T_WHITE) {
      const badges: Array<KLUserBadgeState> = Object.keys(userKLBadges)
        .filter((badgeId) => !!~KLRound2CollectibleIds.indexOf(badgeId))
        .map((badgeId) => userKLBadges[badgeId]);

      return badges.find(
        (badge) =>
          badge.betslipReference === userKLBetslip.betslipReference &&
          userKLBetslip.betslipStatus === BetslipStatuses.RUNNING &&
          !!~userKLBetslip.matchIds.indexOf(basketItemMatchId),
      );
    }

    if (userKLRound === KLRounds.ORIENTATION) {
      const steps: Array<KLUserOrientationCollectibleState> = Object.keys(
        userKLOrientation[userKLOrientation.length - 1],
      )
        .filter((stepId) => !!~KLRound3CollectibleIds.indexOf(stepId))
        .map((stepId) => userKLOrientation[userKLOrientation.length - 1][stepId]);

      return steps.find(
        (step) =>
          step.betslipReference === userKLBetslip.betslipReference &&
          userKLBetslip.betslipStatus === BetslipStatuses.RUNNING &&
          !!~userKLBetslip.matchIds.indexOf(basketItemMatchId),
      );
    }

    if (userKLRound === KLRounds.POLE) {
      const { bets: poleBets } = userKLPole;

      return poleBets.find(
        (poleBet) =>
          poleBet.betslipReference === userKLBetslip.betslipReference &&
          userKLBetslip.betslipStatus === BetslipStatuses.RUNNING &&
          !!~userKLBetslip.matchIds.indexOf(basketItemMatchId),
      );
    }

    return false;
  });

const hasUserKLBetslipMatchingPendingBadge = (
  userKLBadges: KLUserBadgesState,
  eligibleCollectibleIdsFromCriteria: Array<KLCollectibleId>,
) => {
  const eligibleBadgeId = eligibleCollectibleIdsFromCriteria[0];

  if (eligibleBadgeId) {
    return userKLBadges[eligibleBadgeId]?.status === KLCollectibleStatuses.PENDING;
  }

  return false;
};

const hasUserKLBetslipMatchingPendingOrientationStep = (
  userKLOrientation: Array<KLUserOrientationCollectiblesState>,
) => {
  return (
    Object.keys(userKLOrientation[userKLOrientation.length - 1] ?? {})
      .map(
        (key: KLCollectibleId): KLUserOrientationCollectibleState =>
          userKLOrientation[userKLOrientation.length - 1][key],
      )
      .filter((step) => step.status === KLCollectibleStatuses.OPEN || step.status === KLCollectibleStatuses.CANCELLED)
      .length === 0
  );
};

const checkForUserKLBetslipMatchingPendingBetslip = (
  infosLabel: Array<string>,
  userKLBetslipsAsArray: Array<KLUserBetslipState>,
  userKLRound: KLRound,
  userKLBadges: KLUserBadgesState,
  userKLOrientation: Array<KLUserOrientationCollectiblesState>,
  userKLPole: KLUserPoleState,
  basketItemMatchId: number,
) => {
  if (
    hasUserKLBetslipMatchingPendingBetslip(
      userKLBetslipsAsArray,
      userKLRound,
      userKLBadges,
      userKLOrientation,
      userKLPole,
      basketItemMatchId,
    )
  ) {
    infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_MATCH_PENDING]);
  }
};

const checkForUserKLBetslipMatchingPendingBadge = (
  infosLabel: Array<string>,
  userKLBadges: KLUserBadgesState,
  userRound: KLRound,
  eligibleCollectibleIdsFromCriteria: Array<KLCollectibleId>,
) => {
  if (
    (userRound === KLRounds.T_RED || userRound === KLRounds.T_WHITE) &&
    hasUserKLBetslipMatchingPendingBadge(userKLBadges, eligibleCollectibleIdsFromCriteria)
  ) {
    infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_BADGE_PENDING]);
  }
};

const checkForUserKLBetslipMatchingPendingOrientationStep = (
  infosLabel: Array<string>,
  userKLOrientation: Array<KLUserOrientationCollectiblesState>,
  userRound: KLRound,
) => {
  if (userRound === KLRounds.ORIENTATION && hasUserKLBetslipMatchingPendingOrientationStep(userKLOrientation)) {
    infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_MATCH_PENDING_ORIENTATION]);
  }
};

const checkForBasketMode = (infosLabel: Array<string>, basketMode: BasketMode) => {
  if (basketMode === BasketModes.SYSTEM) {
    infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_SYSTEM_FORBIDDEN]);
  }
};

const checkForBasketItemsMatchesStartDate48h = (infosLabel: Array<string>, basketItemsMatches: Array<MatchState>) => {
  for (const basketItemMatch of basketItemsMatches) {
    const nowInSec = Date.now() / 1000;
    const twoDaysInSec = 60 * 60 * 24 * 2;

    if (typeof basketItemMatch.matchStart === 'number' && basketItemMatch.matchStart - nowInSec > twoDaysInSec) {
      infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_START_DATE_OVER_48]);
    }
  }
};

const checkForBasketItemsMatchesStartDateOperationEndDate = (
  infosLabel: Array<string>,
  basketItemsMatches: Array<MatchState>,
  operationEndDate?: number,
) => {
  for (const basketItemMatch of basketItemsMatches) {
    if (
      typeof basketItemMatch.matchStart === 'number' &&
      typeof operationEndDate === 'number' &&
      basketItemMatch.matchStart - operationEndDate > 0
    ) {
      infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_START_DATE_OVER_OPE]);
    }
  }
};

const checkForUserTries = (infosLabel: Array<string>, userKLNbVotes: number, userKLNbCollars: number) => {
  if (userKLNbVotes - userKLNbCollars >= 6) {
    infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_TOO_MANY_TRIES]);
  }
};

const hasBasketActivatedFreebets = (
  basketMode: BasketMode,
  basketItemsState: BasketItems,
  basketSelectedFreebetId: boolean,
  oddsId?: number,
) => {
  if (basketMode === BasketModes.SIMPLE) {
    if (oddsId) {
      return !!basketItemsState[`${oddsId}`]?.selectedFreebetId;
    } else {
      return Object.keys(basketItemsState)
        .map((key) => basketItemsState[key])
        .sort((a, b) => (a?.sortBy ?? 0) - (b?.sortBy ?? 0))[0]?.selectedFreebetId;
    }
  }

  if (basketMode === BasketModes.COMBINED) {
    return !!basketSelectedFreebetId;
  }

  return false;
};

const checkForFreebets = (
  infosLabel: Array<string>,
  basketMode: BasketMode,
  basketItemsState: BasketItems,
  basketSelectedFreebetId: boolean,
  oddsId?: number,
) => {
  if (hasBasketActivatedFreebets(basketMode, basketItemsState, basketSelectedFreebetId, oddsId)) {
    infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_FREEBET_FORBIDDEN]);
  }
};

const ForbiddenBetTypeIds = [
  BetTypes.SOCCER_QUALIFICATION,
  BetTypes.SOCCER_WINNER_BRACKET_FINAL_WINNER,
  BetTypes.SOCCER_LOOSER_BRACKET_FINAL_WINNER,
];

const hasBasketQualifiedBet = (
  basketMode: BasketMode,
  basketItemsState: BasketItems,
  betsState: {
    [key: number]: BetState;
  },
  oddsId?: number,
) => {
  if (basketMode === BasketModes.SIMPLE) {
    const betType =
      typeof oddsId === 'number' && basketItemsState[`${oddsId}`]
        ? betsState[basketItemsState[`${oddsId}`].betId]?.betType
        : betsState[
            Object.keys(basketItemsState)
              .map((key) => basketItemsState[key])
              .sort((a, b) => (a?.sortBy ?? 0) - (b?.sortBy ?? 0))[0]?.betId
          ]?.betType;

    return betType && !!~ForbiddenBetTypeIds.indexOf(betType);
  }

  if (basketMode === BasketModes.COMBINED) {
    return Object.keys(basketItemsState)
      .map((key) => basketItemsState[key]?.betId)
      .filter((k) => k)
      .some((betId) => !!~ForbiddenBetTypeIds.indexOf(betsState[betId]?.betType));
  }

  return false;
};

const checkForBetQualify = (
  infosLabel: Array<string>,
  basketMode: BasketMode,
  basketItemsState: BasketItems,
  betsState: {
    [key: number]: BetState;
  },
  oddsId?: number,
) => {
  if (hasBasketQualifiedBet(basketMode, basketItemsState, betsState, oddsId)) {
    infosLabel.push(KLErrorLabelsByErrorCode[KLBasketErrorCodes.E_BET_QUALIFY_FORBIDDEN]);
  }
};

const selectKLSwitchInfosLabel = createSelector(
  [
    getOddsIdFromProps,
    selectBasketMode,
    selectBasketItems,
    selectKLUserBetslips,
    selectKLUserBadges,
    selectKLUserOrientation,
    selectKLUserPole,
    selectBasketItemsMatches,
    selectKLOperationEndDate,
    selectKLUserVotes,
    selectKLUserCollars,
    selectUserEligibilities,
    selectBasketSelectedFreebetId,
    selectEntitiesBets,
    selectKLUserRound,
    selectEligibleCollectibleIdsFromCriteria,
  ],
  (
    oddsId,
    basketMode,
    basketItemsState,
    userKLBetslips,
    userKLBadges,
    userKLOrientation,
    userKLPole,
    basketItemsMatches,
    operationEndDate,
    userKLNbVotes,
    userKLNbCollars,
    eligibilities,
    basketSelectedFreebetId,
    betsState,
    userKLRound,
    eligibleCollectibleIdsFromCriteria,
  ) => {
    const infosLabel = [];
    const userKLBetslipsAsArray = Object.keys(userKLBetslips).map((key) => userKLBetslips[key]);

    checkForBasketMode(infosLabel, basketMode);
    checkForFreebets(infosLabel, basketMode, basketItemsState, basketSelectedFreebetId, oddsId);
    checkForBasketItemsMatchesStartDate48h(infosLabel, basketItemsMatches);
    checkForBasketItemsMatchesStartDateOperationEndDate(infosLabel, basketItemsMatches, operationEndDate);
    checkForUserTries(infosLabel, userKLNbVotes, userKLNbCollars);
    checkForUserKLBetslipMatchingPendingOrientationStep(infosLabel, userKLOrientation, userKLRound);
    checkForUserKLBetslipMatchingPendingBadge(
      infosLabel,
      userKLBadges,
      userKLRound,
      eligibleCollectibleIdsFromCriteria,
    );
    checkForBetQualify(infosLabel, basketMode, basketItemsState, betsState, oddsId);

    if (oddsId) {
      const basketItem = basketItemsState[oddsId];

      if (!basketItem) {
        return;
      }
      checkForUserKLBetslipMatchingPendingBetslip(
        infosLabel,
        userKLBetslipsAsArray,
        userKLRound,
        userKLBadges,
        userKLOrientation,
        userKLPole,
        basketItem.matchId,
      );
    } else {
      const basketItems = Object.keys(basketItemsState).map((key) => basketItemsState[key]);

      for (const item of basketItems) {
        const { matchId: basketItemMatchId } = item;
        checkForUserKLBetslipMatchingPendingBetslip(
          infosLabel,
          userKLBetslipsAsArray,
          userKLRound,
          userKLBadges,
          userKLOrientation,
          userKLPole,
          basketItemMatchId,
        );
      }
    }

    return infosLabel;
  },
);

const selectKLBasketSwitchOnToggle = createSelector(
  [
    getDispatchFromProps,
    getOddsIdFromProps,
    selectEligibleCollectibleIdsFromCriteria,
    selectBasketMode,
    selectBasketItems,
    selectKLSwitchInfosLabel,
  ],
  (dispatch, oddsId, eligibleCollectibleIdsFromCriteria, basketMode, basketItems, infosLabel) => (isSelected) => {
    if (!infosLabel.length) {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

      if (oddsId) {
        dispatch(
          basketItemSetKLEligibleCollectible({
            oddsId,
            collectibleId: isSelected ? eligibleCollectibleIdsFromCriteria[0] ?? null : null,
          }),
        );
      } else {
        if (isOneSimpleBet) {
          const foundOddsId = Object.keys(basketItems)[0];

          if (foundOddsId) {
            dispatch(
              basketItemSetKLEligibleCollectible({
                oddsId: foundOddsId,
                collectibleId: isSelected ? eligibleCollectibleIdsFromCriteria[0] ?? null : null,
              }),
            );
          }
        } else {
          dispatch(
            basketSetKLEligibleCollectible({
              collectibleId: isSelected ? eligibleCollectibleIdsFromCriteria[0] ?? null : null,
            }),
          );
        }
      }
    }
  },
);

const selectHasEligibleBadgeAlreadyBeenWon = createSelector(
  [selectEligibleCollectibleIdsFromCriteria, selectKLUserBadges],
  (eligibleCollectibleIdsFromCriteria, userKLBadges) => {
    if (eligibleCollectibleIdsFromCriteria.length) {
      const [eligibleId] = eligibleCollectibleIdsFromCriteria;

      if (
        (!!~KLRound1CollectibleIds.indexOf(eligibleId) || !!~KLRound2CollectibleIds.indexOf(eligibleId)) &&
        userKLBadges
      ) {
        return (
          userKLBadges[eligibleId]?.status === KLCollectibleStatuses.WON ||
          userKLBadges[eligibleId]?.status === KLCollectibleStatuses.CANCELLED
        );
      }
    }

    return false;
  },
);

const selectKLBasketSwitchIsVisible = createSelector(
  [
    selectKLUserStatus,
    selectBasketShowResume,
    getOddsIdFromProps,
    selectBasketMode,
    selectBasketEnhancedItems,
    selectUserConnected,
    selectIsKLEnabled,
    selectBasketIsCashout,
    selectKLGlobalStatus,
    selectKLSwitchInfosLabel,
    selectHasEligibleBadgeAlreadyBeenWon,
    selectEligibleCollectibleIdsFromCriteria,
  ],
  (
    userStatus,
    showResume,
    oddsId,
    basketMode,
    items,
    userIsConnected,
    isKLEnabled,
    isCashout,
    KLGlobalStatus,
    infosLabel,
    hasEligibleBadgeAlreadyBeenWon,
    eligibleCollectibleIdsFromCriteria,
  ) => {
    if (
      isKLEnabled &&
      KLGlobalStatus === KLGlobalStatuses.RUNNING &&
      userIsConnected &&
      userStatus === KLUserStatuses.IN &&
      !showResume &&
      !hasEligibleBadgeAlreadyBeenWon
    ) {
      const isModeSimple = basketMode === BasketModes.SIMPLE;
      const isModeSystem = basketMode === BasketModes.SYSTEM;

      const isOneSimpleBet = isModeSimple && items.length === 1;
      const isMultipleSimpleBets = isModeSimple && items.length > 1;

      return (
        infosLabel.length <= 1 &&
        eligibleCollectibleIdsFromCriteria.length &&
        !isCashout &&
        !isModeSystem &&
        ((isOneSimpleBet && !oddsId) || (!isModeSimple && !oddsId) || (isMultipleSimpleBets && oddsId))
      );
    }

    return false;
  },
);

const selectKLBasketSwitchIsDisabled = createSelector(
  [selectBasketStatus, selectEligibleCollectibleIdsFromCriteria, selectKLSwitchInfosLabel],
  (basketStatus, eligibleCollectibleIdsFromCriteria, infosLabel) =>
    basketStatus === BasketStatuses.PENDING || !eligibleCollectibleIdsFromCriteria.length || !!infosLabel.length,
);

const selectKLBasketSwitchIsToggled = createSelector(
  [selectBasketMode, getOddsIdFromProps, selectBasketItems, selectBasketState],
  (basketMode, oddsId, basketItems, basket) => {
    const isModeSimple = basketMode === BasketModes.SIMPLE;
    const isModeCombined = basketMode === BasketModes.COMBINED;
    const isOneSimpleBet = isModeSimple && Object.keys(basketItems).length === 1;

    if (isOneSimpleBet) {
      const foundOddsId = Object.keys(basketItems)[0];

      return !!basketItems[foundOddsId]?.eligibleKLCollectibleId;
    }
    if (isModeSimple) {
      return !!basketItems[oddsId]?.eligibleKLCollectibleId;
    }
    if (isModeCombined) {
      return !!basket.eligibleKLCollectibleId;
    }
  },
);

const selectKLBasketSwitchProps = createSelector(
  [
    selectKLBasketSwitchIsVisible,
    selectKLBasketSwitchIsDisabled,
    selectKLBasketSwitchIsToggled,
    selectKLBasketSwitchOnToggle,
    selectKLSwitchInfosLabel,
  ],
  (isVisible, disabled, toggled, onToggle, infosLabel) => ({
    isVisible,
    disabled,
    toggled,
    onToggle,
    infosLabel: infosLabel[0],
  }),
);

export default selectKLBasketSwitchProps;
